import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog.js";
import { Button } from "./ui/button.js";
import { Badge } from "./ui/badge.js";
import { Switch } from "./ui/switch.js";
import { AlertTriangle, CheckCircle2, Send, FileText, Info, Mail, ChevronDown, ChevronUp } from "lucide-react";

export interface EmailTargetItem {
  idx: number;
  receptorNombre?: string;
  receptorRut?: string;
  correo: string;
}

export interface ConfirmEmitOptions {
  sendSiiEmail: boolean;
  excludedEmailIndices: number[];
}

export interface EmitSummaryItem {
  tab: "bhe" | "be" | "factura" | "nc";
  documentName: string;
  totalCount: number;
  totalMonto: number;
  distinctEmisores: string[];
  beBreakdown?: {
    sin_nada: number;
    solo_glosa: number;
    receptor_sin_detalle: number;
    completa: number;
  };
  emailTargets?: EmailTargetItem[];
}

interface ConfirmEmitModalProps {
  open: boolean;
  summary: EmitSummaryItem | null;
  onClose: () => void;
  onConfirm: (options: ConfirmEmitOptions) => void;
}

export default function ConfirmEmitModal({
  open,
  summary,
  onClose,
  onConfirm,
}: ConfirmEmitModalProps) {
  const [sendSiiEmail, setSendSiiEmail] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("foliolisto_send_sii_email");
      return saved !== null ? saved === "true" : true;
    } catch {
      return true;
    }
  });
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());
  const [showRecipientList, setShowRecipientList] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setExcludedIndices(new Set());
      setShowRecipientList(false);
    }
  }, [open, summary]);

  if (!summary) return null;

  const formattedMonto = new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(summary.totalMonto);

  const hasAnonimas = summary.tab === "be" && (summary.beBreakdown?.sin_nada ?? 0) > 0;
  const emailTargets = summary.emailTargets ?? [];
  const hasEmailTargets = emailTargets.length > 0;
  const activeEmailCount = sendSiiEmail
    ? emailTargets.filter((t) => !excludedIndices.has(t.idx)).length
    : 0;

  const handleToggleEmail = (checked: boolean) => {
    setSendSiiEmail(checked);
    try {
      localStorage.setItem("foliolisto_send_sii_email", String(checked));
    } catch {
      // Ignore local storage error
    }
  };

  const handleConfirm = () => {
    onConfirm({
      sendSiiEmail,
      excludedEmailIndices: Array.from(excludedIndices),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">
                Confirmar emisión de {summary.documentName}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Revisa el resumen antes de generar los documentos en el SII.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3.5 py-2">
          {/* Key Metrics Card */}
          <div className="grid grid-cols-2 gap-2.5 rounded-lg border border-border/70 bg-secondary/20 p-3.5">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                Documentos
              </span>
              <p className="text-xl font-bold text-foreground mt-0.5">
                {summary.totalCount} {summary.totalCount === 1 ? "documento" : "documentos"}
              </p>
            </div>
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                Monto Total
              </span>
              <p className="text-xl font-bold text-primary mt-0.5">
                {formattedMonto}
              </p>
            </div>
            {summary.distinctEmisores.length > 0 && (
              <div className="col-span-2 pt-2 border-t border-border/40 flex items-center justify-between text-xs text-muted-foreground">
                <span>RUT Emisor:</span>
                <span className="font-mono font-medium text-foreground">
                  {summary.distinctEmisores.length === 1
                    ? summary.distinctEmisores[0]
                    : `${summary.distinctEmisores.length} emisores distintos`}
                </span>
              </div>
            )}
          </div>

          {/* BE Breakdown */}
          {summary.tab === "be" && summary.beBreakdown && (
            <div className="space-y-2">
              <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <Info className="h-3.5 w-3.5 text-primary" />
                Desglose por Modo de Emisión
              </span>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {summary.beBreakdown.completa > 0 && (
                  <div className="rounded-md border border-purple-500/30 bg-purple-500/10 p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-purple-600 dark:text-purple-400">🌟 Completa</span>
                      <Badge variant="secondary" className="text-[10px] font-bold">
                        {summary.beBreakdown.completa}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">Con Receptor y Detalle</p>
                  </div>
                )}
                {summary.beBreakdown.receptor_sin_detalle > 0 && (
                  <div className="rounded-md border border-blue-500/30 bg-blue-500/10 p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-blue-600 dark:text-blue-400">👤 Con Receptor</span>
                      <Badge variant="secondary" className="text-[10px] font-bold">
                        {summary.beBreakdown.receptor_sin_detalle}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">Nominativa sin Detalle</p>
                  </div>
                )}
                {summary.beBreakdown.solo_glosa > 0 && (
                  <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">💬 Con Detalle</span>
                      <Badge variant="secondary" className="text-[10px] font-bold">
                        {summary.beBreakdown.solo_glosa}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">Detalle sin Receptor</p>
                  </div>
                )}
                {summary.beBreakdown.sin_nada > 0 && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/15 p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-amber-600 dark:text-amber-400">⚡ Solo Monto</span>
                      <Badge variant="warning" className="text-[10px] font-bold">
                        {summary.beBreakdown.sin_nada}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">Consumidor Final (anónima)</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Native SII Email Dispatch Card */}
          {hasEmailTargets && (
            <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 dark:bg-sky-500/10 p-3 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sky-500/20 text-sky-600 dark:text-sky-400">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                      Envío automático por correo vía SII
                      <Badge variant="outline" className="text-[10px] py-0 px-1 border-sky-500/30 text-sky-600 dark:text-sky-400 font-mono">
                        {activeEmailCount}/{emailTargets.length}
                      </Badge>
                    </span>
                    <p className="text-[11px] text-muted-foreground leading-snug">
                      Despacho oficial de boleta directo desde el servidor del SII.
                    </p>
                  </div>
                </div>
                <Switch
                  id="toggle-sii-email"
                  checked={sendSiiEmail}
                  onCheckedChange={handleToggleEmail}
                />
              </div>

              {sendSiiEmail ? (
                <div className="pt-1 border-t border-sky-500/20 text-xs space-y-2">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setShowRecipientList((v) => !v)}
                      className="text-[11px] font-medium text-sky-600 dark:text-sky-400 hover:underline flex items-center gap-1"
                    >
                      {showRecipientList ? "Ocultar destinatarios" : "Ver / personalizar destinatarios"}
                      {showRecipientList ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                    {showRecipientList && (
                      <div className="flex items-center gap-2 text-[10px]">
                        <button
                          type="button"
                          onClick={() => setExcludedIndices(new Set())}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          Marcar todos
                        </button>
                        <span>•</span>
                        <button
                          type="button"
                          onClick={() => setExcludedIndices(new Set(emailTargets.map((t) => t.idx)))}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          Desmarcar todos
                        </button>
                      </div>
                    )}
                  </div>

                  {showRecipientList && (
                    <div className="max-h-36 overflow-y-auto space-y-1 pr-1 border border-border/50 rounded-md p-1.5 bg-background/50">
                      {emailTargets.map((target) => {
                        const isChecked = !excludedIndices.has(target.idx);
                        return (
                          <label
                            key={target.idx}
                            className="flex items-center justify-between text-[11px] py-1 px-1.5 rounded hover:bg-secondary/30 cursor-pointer select-none"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  const next = new Set(excludedIndices);
                                  if (e.target.checked) next.delete(target.idx);
                                  else next.add(target.idx);
                                  setExcludedIndices(next);
                                }}
                                className="h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary"
                              />
                              <span className="font-medium truncate text-foreground">
                                {target.receptorNombre || target.receptorRut || `Fila #${target.idx + 1}`}
                              </span>
                            </div>
                            <span className="text-[10px] text-muted-foreground font-mono truncate ml-2">
                              {target.correo}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground italic">
                  Desactivado. Las boletas se emitirán en el SII sin enviar correos a los destinatarios.
                </p>
              )}
            </div>
          )}

          {/* Audit Alert */}
          {hasAnonimas ? (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-amber-700 dark:text-amber-300 flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
              <div className="text-xs space-y-1">
                <p className="font-semibold">
                  Detectamos {summary.beBreakdown?.sin_nada} boleta(s) anónima(s)
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Se emitirán como <strong>Consumidor Final</strong> (sin RUT ni Nombre de cliente). Si tu intención era emitirlas a clientes específicos, pulsa <strong>"Volver a la tabla"</strong> para ingresar sus datos.
                </p>
              </div>
            </div>
          ) : summary.tab === "be" ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-2.5 text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
              <p className="text-xs">
                Todas las boletas incluyen la información nominativa requerida.
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Volver a la tabla
          </Button>
          <Button variant="cta" onClick={handleConfirm} className="gap-1.5">
            <Send className="h-4 w-4" />
            Confirmar y Continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
